<template>
	<div v-if="isVisible" class="lot-allocations-editor">
		<div class="d-flex align-center mb-1">
			<v-subheader class="pl-0">{{ isSerialMode ? 'Matricole' : 'Lotti' }}</v-subheader>
			<v-spacer></v-spacer>
			<v-chip small :color="remainder === 0 ? 'success' : 'warning'" outlined>
				{{ isSerialMode ? assignedCount + '/' + targetQuantity : 'Da assegnare: ' + remainder }}
			</v-chip>
			<v-btn v-if="!isSerialMode" small text color="primary" class="ml-2" :disabled="!options.length" @click="autoAssignFefo">
				<v-icon small left>mdi-auto-fix</v-icon>Assegna automaticamente
			</v-btn>
		</div>

		<template v-if="!isSerialMode">
			<v-row v-for="(allocation, index) in allocations" :key="index" dense align="center">
				<v-col cols="7">
					<v-autocomplete
						v-model="allocation.lot_id"
						:items="availableOptions(allocation)"
						:item-disabled="itemDisabled"
						item-value="value"
						item-text="text"
						label="Lotto"
						outlined
						dense
						hide-details
						@change="onLotChange(allocation)"
					>
						<template v-slot:item="{ item }">
							<v-list-item-content>
								<v-list-item-title>{{ item.text }}</v-list-item-title>
								<v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
							</v-list-item-content>
						</template>
					</v-autocomplete>
				</v-col>
				<v-col cols="4">
					<v-text-field
						v-model.number="allocation.quantity"
						type="number"
						label="Quantità"
						outlined
						dense
						hide-details
						min="0"
						@input="syncModel"
					></v-text-field>
				</v-col>
				<v-col cols="1">
					<v-btn icon small color="error" @click="removeAllocation(index)">
						<v-icon small>mdi-delete</v-icon>
					</v-btn>
				</v-col>
			</v-row>
			<v-btn small text color="primary" class="mt-1" @click="addAllocation">
				<v-icon small left>mdi-plus</v-icon>Aggiungi lotto
			</v-btn>
			<!-- Sempre disponibile, anche in scarico: e' il comportamento storico del campo lotto
			     (chi non carica i lotti a sistema li indica direttamente sul documento di vendita).
			     Il vincolo "solo codici esistenti" resta invece sulle matricole. -->
			<v-btn v-if="allowCreate" small text color="primary" class="mt-1" :disabled="!model.product_id" @click="openNewLotDialog">
				<v-icon small left>mdi-tag-plus</v-icon>Nuovo lotto
			</v-btn>
		</template>

		<template v-else>
			<v-autocomplete
				v-model="selectedSerialOption"
				:items="availableSerialOptions"
				:item-disabled="serialItemDisabled"
				item-value="value"
				item-text="text"
				label="Seleziona matricola"
				outlined
				dense
				hide-details
				class="mb-2"
				@change="onSerialOptionSelected"
			>
				<template v-slot:item="{ item }">
					<v-list-item-content>
						<v-list-item-title>{{ item.text }}</v-list-item-title>
						<v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
					</v-list-item-content>
				</template>
			</v-autocomplete>
			<v-text-field
				v-model="serialInput"
				:label="isEffectiveUnload || !allowCreate ? 'Scansiona o digita una matricola esistente' : 'Matricola (Invio per aggiungere)'"
				outlined
				dense
				hide-details
				append-icon="mdi-barcode-scan"
				@keydown.enter.prevent="addSerial"
			></v-text-field>
			<v-chip-group column class="mt-1">
				<v-chip
					v-for="(allocation, index) in allocations"
					:key="index"
					small
					close
					@click:close="removeAllocation(index)"
				>{{ allocation.lot_id_text }}</v-chip>
			</v-chip-group>
		</template>

		<v-dialog v-model="newLotDialog" max-width="420" persistent>
			<v-card>
				<v-card-title class="subtitle-1">Nuovo lotto</v-card-title>
				<v-card-text>
					<v-text-field
						v-model="newLot.code"
						label="Codice"
						outlined
						dense
						autofocus
						:error-messages="newLotError"
						@keydown.enter.prevent="createLot"
					></v-text-field>
					<gmh-date-picker
						v-model="newLot.expiration_date"
						label="Scadenza"
						hide-details
						editable
						:clear-value="null"
					></gmh-date-picker>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text :disabled="creatingLot" @click="closeNewLotDialog">Annulla</v-btn>
					<v-btn color="primary" :loading="creatingLot" @click="createLot">Crea</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<div v-if="stockWarning" class="warning--text caption mt-1">
			<v-icon small color="warning">mdi-alert</v-icon> {{ stockWarning }}
		</div>
		<div v-if="errorMessage" class="error--text caption mt-1">{{ errorMessage }}</div>
	</div>
</template>

<script>
export default {
	name: 'LotAllocationsEditor',
	props: {
		field: { type: Object, required: true },
		model: { type: Object, required: true }
	},
	data() {
		return {
			allocations: [],
			serialInput: '',
			selectedSerialOption: null,
			errorMessage: null,
			creatingSerial: false,
			newLotDialog: false,
			creatingLot: false,
			newLotError: null,
			newLot: { code: '', expiration_date: null }
		}
	},
	computed: {
		isVisible() {
			// il fieldset dichiara ora un solo campo 'lot_allocations' (nessun depends):
			// è il componente stesso a decidere se rendersi visibile, in base al prodotto
			// selezionato sulla riga (lots o serials), non più al depends_key del field.
			return !!(this.model.lots || this.model.serials)
		},
		isConversion() {
			// opzioni dal documento d'origine di una conversione: portano il residuo
			return this.options.some((o) => o.residual !== undefined && o.residual !== null)
		},
		allowCreate() {
			return this.field.allow_create !== false
		},
		options() {
			return this.field.options || []
		},
		isSerialMode() {
			return !!this.model.serials
		},
		targetQuantity() {
			// le righe possono avere quantità negativa (resi/storni, il segno inverte
			// la direzione del movimento lato server): la quantità da assegnare ai
			// lotti/matricole è comunque il valore assoluto
			return Math.abs(parseFloat(this.model.quantity)) || 0
		},
		isEffectiveUnload() {
			// direzione EFFETTIVA della riga: il segno della quantità inverte la
			// direzione nominale del tipo di riga, come lato server
			// (RowLotAllocationSyncer::isEffectiveUnloadRow)
			const isNegativeQuantity = parseFloat(this.model.quantity) < 0
			return (this.field.row_direction === 'unload') !== isNegativeQuantity
		},
		availableSerialOptions() {
			// matricole non ancora selezionate in questa riga; se le opzioni sono miste
			// (lotti + matricole) restano solo quelle di tipo 'serial'
			const used = this.allocations.map((a) => a.lot_id)
			return this.options.filter((o) => o.lot_type === 'serial' && !used.includes(o.value))
		},
		assignedTotal() {
			return this.allocations.reduce((sum, a) => sum + (parseFloat(a.quantity) || 0), 0)
		},
		assignedCount() {
			return this.allocations.length
		},
		remainder() {
			return Math.round((this.targetQuantity - this.assignedTotal) * 10000) / 10000
		},
		documentSupplierId() {
			// riferimento reattivo al documento padre (non un valore snapshot):
			// letto ad ogni creazione, mai cacheato, così riflette il fornitore anche
			// se viene impostato dopo l'apertura della riga. Iniettato da
			// CrudTableRelationship.updateLotOptions() solo sui documenti di acquisto.
			return this.field.document_parent ? this.field.document_parent.company_id : null
		},
		stockWarning() {
			// in modalità matricole ogni matricola appena creata parte da giacenza 0:
			// il confronto quantità/giacenza sarebbe sempre "oltre giacenza", un falso
			// positivo permanente. Il warning non ha senso in questa modalità.
			if (this.isSerialMode) return null
			// il warning di giacenza ha senso solo quando la direzione EFFETTIVA della
			// riga è uno scarico: un carico (anche ottenuto da uno scarico nominale con
			// quantità negativa) non consuma giacenza e non deve mostrarlo
			if (!this.isEffectiveUnload) return null
			// le quantità sono nell'UM della riga, la giacenza del lotto in UM base
			const unitFactor = parseFloat(this.model.unit_factor) || 1
			const over = this.allocations.filter((a) => {
				const opt = this.options.find((o) => o.value === a.lot_id)
				if (!opt) return false
				const stock = parseFloat(opt.stock)
				if (isNaN(stock)) return false
				return parseFloat(a.quantity) * unitFactor > stock
			})
			if (!over.length) return null
			return 'Quantità superiore alla giacenza per: ' + over.map((a) => a.lot_id_text).join(', ')
		}
	},
	watch: {
		'model.quantity'() {
			// parità col comportamento attuale: con allocazione unica segue la quantità riga
			if (!this.isSerialMode && this.allocations.length === 1) {
				this.allocations[0].quantity = this.targetQuantity
				this.syncModel()
			}
		},
		'model.product_id'() {
			this.loadFromModel()
		}
	},
	created() {
		this.loadFromModel()
		this.hydrateFromScalarFallback()
	},
	methods: {
		loadFromModel() {
			this.allocations = (this.model[this.field.key] || []).map((a) => ({
				lot_id: a.lot_id,
				lot_id_text: a.lot_id_text || a.code,
				quantity: parseFloat(a.quantity) || 0
			}))
		},
		hydrateFromScalarFallback() {
			// cintura di sicurezza: se l'editor monta dopo che il fallback scalare legacy
			// ha già valorizzato lot_id (es. delega non ancora arrivata, storico prodotti,
			// barcode), idrata una singola allocazione equivalente così l'editor non appare
			// vuoto mentre lot_id/lot_id_text sono già impostati. Non fa nulla se model[field.key]
			// contiene già allocazioni esplicite.
			if (this.allocations.length > 0) return
			if (!this.model.lot_id) return
			if (!(this.targetQuantity > 0)) return
			this.allocations = [{ lot_id: this.model.lot_id, lot_id_text: this.model.lot_id_text, quantity: this.targetQuantity }]
			this.syncModel()
		},
		availableOptions(current) {
			const used = this.allocations.filter((a) => a !== current).map((a) => a.lot_id)
			return this.options.filter((o) => !used.includes(o.value))
		},
		itemDisabled(item) {
			return !!item.barred
		},
		serialItemDisabled(item) {
			// in scarico effettivo non si può attingere a una matricola già senza giacenza
			if (!this.isEffectiveUnload) return false
			const stock = parseFloat(item.stock)
			return isNaN(stock) || stock <= 0
		},
		addAllocation() {
			this.allocations.push({ lot_id: null, lot_id_text: null, quantity: Math.max(this.remainder, 0) })
		},
		openNewLotDialog() {
			this.newLot = { code: '', expiration_date: null }
			this.newLotError = null
			this.newLotDialog = true
		},
		closeNewLotDialog() {
			this.newLotDialog = false
		},
		createLot() {
			const code = (this.newLot.code || '').trim()
			if (!code) {
				this.newLotError = 'Il codice del lotto è obbligatorio'
				return Promise.resolve()
			}
			if (this.options.some((o) => o.text === code)) {
				this.newLotError = 'Esiste già un lotto con questo codice'
				return Promise.resolve()
			}
			if (this.creatingLot) return Promise.resolve()

			this.creatingLot = true
			this.newLotError = null
			const payload = {
				code: code,
				product_id: this.model.product_id,
				expiration_date: this.newLot.expiration_date || null,
				type: 'lot',
				stock: 0
			}
			if (this.documentSupplierId) payload.company_id = this.documentSupplierId
			return this.axios.post('lots', payload).then((response) => {
				const id = response.data.id || (response.data.model && response.data.model.id)
				this.field.options.push({
					value: id,
					text: code,
					stock: 0,
					lot_type: 'lot',
					expiration_date: this.newLot.expiration_date || null
				})
				// il lotto appena creato ha giacenza 0: prende il residuo corrente della riga
				this.allocations.push({ lot_id: id, lot_id_text: code, quantity: Math.max(this.remainder, 0) })
				this.newLotDialog = false
				this.errorMessage = null
				this.syncModel()
			}).catch(() => {
				this.newLotError = 'Errore nella creazione del lotto'
			}).finally(() => {
				this.creatingLot = false
			})
		},
		removeAllocation(index) {
			this.allocations.splice(index, 1)
			this.syncModel()
		},
		onLotChange(allocation) {
			const opt = this.options.find((o) => o.value === allocation.lot_id)
			allocation.lot_id_text = opt ? opt.text : null
			this.syncModel()
		},
		validLots() {
			const today = new Date().toISOString().slice(0, 10)
			// stock arriva dal backend come colonna FLOAT grezza: a seconda del driver
			// PDO può arrivare come stringa, o mancare del tutto per opzioni costruite
			// altrove (es. matricola creata al volo). parseFloat difensivo per non far
			// passare lotti con giacenza non numerica o <= 0.
			return this.options.filter((o) => {
				const stock = parseFloat(o.stock)
				return !o.barred &&
					!isNaN(stock) && stock > 0 &&
					(!o.expiration_date || o.expiration_date >= today)
			})
		},
		prefillFirstLot() {
			// parità con autoselectFirstLot: primo lotto valido, tutta la quantità
			if (this.allocations.length > 0) return
			const lot = this.validLots()[0]
			if (!lot) return
			this.allocations = [{ lot_id: lot.value, lot_id_text: lot.text, quantity: this.targetQuantity }]
			this.syncModel()
		},
		lotStockBlocking() {
			// come RowLotAllocationSyncer: la giacenza del lotto blocca lo scarico
			// solo se il tenant lo ha scelto nelle impostazioni dei lotti
			return !!this.$store.getters.getSetting('lot_stock_check_blocking')
		},
		conversionLots() {
			// pagine di conversione (evasioni, resi): le opzioni portano il residuo della
			// riga d'origine, che fa da tetto; negli scarichi si escludono i lotti scaduti
			// e, se il tenant blocca sulla giacenza, quelli senza giacenza (in un reso da
			// cliente il lotto e' uscito, la sua giacenza e' spesso zero)
			const today = new Date().toISOString().slice(0, 10)
			return this.options
				.filter((o) => {
					if (o.barred || !(parseFloat(o.residual) > 0)) return false
					if (!this.isEffectiveUnload) return true
					if (o.expiration_date && o.expiration_date < today) return false
					if (!this.lotStockBlocking()) return true
					const stock = parseFloat(o.stock)
					return !isNaN(stock) && stock > 0
				})
				.map((o, index) => ({ o, index }))
				.sort((a, b) => {
					// prima i lotti che scadono prima, a parita' l'ordine della riga d'origine
					const ea = a.o.expiration_date || '9999-12-31'
					const eb = b.o.expiration_date || '9999-12-31'
					return ea === eb ? a.index - b.index : (ea < eb ? -1 : 1)
				})
				.map((x) => x.o)
		},
		autoAssignCap(lot) {
			if (!this.isConversion) return parseFloat(lot.stock)
			const residual = parseFloat(lot.residual)
			return this.isEffectiveUnload && this.lotStockBlocking() ? Math.min(residual, parseFloat(lot.stock)) : residual
		},
		autoAssignFefo() {
			let remaining = this.targetQuantity
			const result = []
			const lots = this.isConversion ? this.conversionLots() : this.validLots()
			for (const lot of lots) {
				if (remaining <= 0) break
				const stock = this.autoAssignCap(lot)
				// validLots() scarta già stock non numerico/<= 0: cintura di sicurezza qui,
				// senza la quale un singolo NaN propagherebbe (NaN - remaining = NaN, e
				// 'NaN <= 0' è sempre false) collassando l'assegnazione su un solo lotto
				// invece di splittare sui lotti successivi.
				if (isNaN(stock) || stock <= 0) continue
				const q = Math.min(stock, remaining)
				result.push({ lot_id: lot.value, lot_id_text: lot.text, quantity: q })
				remaining = Math.round((remaining - q) * 10000) / 10000
			}
			if (!result.length) {
				// nessun lotto valido: non azzerare le allocazioni esistenti, l'operatore
				// deve poter continuare ad assegnare a mano quello che c'era già
				this.errorMessage = 'Nessun lotto disponibile per l\'assegnazione automatica'
				return
			}
			this.allocations = result
			this.syncModel()
		},
		addSerial() {
			const raw = (this.serialInput || '').trim()
			if (!raw || this.creatingSerial) return
			// incolla multiplo: più codici separati da spazi/righe vengono accodati uno alla volta
			const codes = raw.split(/\s+/).filter(Boolean)
			if (codes.length > 1) {
				this.serialInput = ''
				codes.reduce((chain, c) => chain.then(() => this.addSingleSerial(c)), Promise.resolve())
				return
			}
			this.addSingleSerial(codes[0])
		},
		onSerialOptionSelected(value) {
			if (!value) return
			const opt = this.options.find((o) => o.value === value)
			this.selectedSerialOption = null
			if (!opt) return
			if (this.allocations.length >= this.targetQuantity) {
				this.errorMessage = 'Hai già indicato tutte le matricole per la quantità della riga'
				return
			}
			this.allocations.push({ lot_id: opt.value, lot_id_text: opt.text, quantity: 1 })
			this.errorMessage = null
			this.syncModel()
		},
		addSingleSerial(code) {
			if (!code) return Promise.resolve()
			if (this.allocations.length >= this.targetQuantity) {
				this.errorMessage = 'Hai già indicato tutte le matricole per la quantità della riga'
				return Promise.resolve()
			}
			const existing = this.options.find((o) => o.text === code)
			if (existing) {
				if (this.allocations.some((a) => a.lot_id === existing.value)) {
					this.errorMessage = 'Matricola già inserita'
					return Promise.resolve()
				}
				if (this.isEffectiveUnload) {
					const stock = parseFloat(existing.stock)
					if (isNaN(stock) || stock <= 0) {
						this.errorMessage = 'Matricola non disponibile: ' + code
						return Promise.resolve()
					}
				}
				this.allocations.push({ lot_id: existing.value, lot_id_text: existing.text, quantity: 1 })
				this.afterSerialAdded()
				return Promise.resolve()
			}
			if (!this.allowCreate) {
				this.errorMessage = 'Matricola non disponibile per questa riga: ' + code
				return Promise.resolve()
			}
			if (this.isEffectiveUnload) {
				// in scarico non si crea una matricola nuova al volo: deve esistere già
				this.errorMessage = 'Matricola inesistente: ' + code
				return Promise.resolve()
			}
			// creazione al volo (solo in carico)
			this.creatingSerial = true
			const payload = {
				code: code,
				product_id: this.model.product_id,
				type: 'serial',
				stock: 0
			}
			if (this.documentSupplierId) payload.company_id = this.documentSupplierId
			return this.axios.post('lots', payload).then((response) => {
				const id = response.data.id || (response.data.model && response.data.model.id)
				this.allocations.push({ lot_id: id, lot_id_text: code, quantity: 1 })
				this.field.options.push({ value: id, text: code, stock: 0, lot_type: 'serial' })
				this.afterSerialAdded()
			}).catch(() => {
				this.errorMessage = 'Errore nella creazione della matricola'
			}).finally(() => {
				this.creatingSerial = false
			})
		},
		afterSerialAdded() {
			this.serialInput = ''
			this.errorMessage = null
			this.syncModel()
		},
		syncModel() {
			const clean = this.allocations.filter((a) => a.lot_id)
			this.$set(this.model, this.field.key, clean.map((a) => ({
				lot_id: a.lot_id,
				lot_id_text: a.lot_id_text,
				quantity: parseFloat(a.quantity) || 0
			})))
			// mirror scalare per pricing lot-aware, colonna tabella e retrocompatibilità
			if (clean.length === 1) {
				this.$set(this.model, 'lot_id', clean[0].lot_id)
				this.$set(this.model, 'lot_id_text', clean[0].lot_id_text)
			} else if (clean.length > 1) {
				this.$set(this.model, 'lot_id', null)
				this.$set(this.model, 'lot_id_text', clean.length + (this.isSerialMode ? ' matricole' : ' lotti'))
			} else {
				this.$set(this.model, 'lot_id', null)
				this.$set(this.model, 'lot_id_text', null)
			}
			this.$emit('allocations-changed', clean)
		},
		validate() {
			const clean = this.allocations.filter((a) => a.lot_id)
			if (!clean.length) {
				return { valid: true, message: null } // lotto facoltativo: parità col comportamento attuale
			}
			if (this.remainder !== 0) {
				const message = this.isSerialMode
					? 'Indica una matricola per ogni pezzo (' + this.assignedCount + '/' + this.targetQuantity + ')'
					: 'La quantità assegnata ai lotti non corrisponde alla quantità della riga (residuo: ' + this.remainder + ')'
				this.errorMessage = message
				return { valid: false, message }
			}
			// Niente blocco giacenza lato client: qui non conosciamo la direzione del
			// movimento della riga (isUnloadRow è server-side, guarda tipo/subtype del
			// documento), quindi non possiamo distinguere scarico da carico. Il flusso
			// standard di ricevimento merce (DDT acquisto, lotto nuovo creato al volo a
			// stock 0, allocazione dell'intera quantità) verrebbe bloccato ogni volta che
			// il setting è attivo. Resta solo lo stockWarning non bloccante (già soppresso
			// in modalità matricole); l'enforcement vero resta il 422 del server, che è
			// direction-aware (RowLotAllocationSyncer::isUnloadRow).
			this.errorMessage = null
			return { valid: true, message: null }
		}
	}
}
</script>
